// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// compassMongoFilter — build a MongoDB find filter for a Compass quick-search
// field (#191). PREFIX, type-agnostic.
//
// Quick search is a "begins with" box: typing "206" in Invoice # should find
// every invoice that STARTS with 206, not just an exact match. Two wrinkles:
//   1. several fields store NUMBERS (invoiceNo + Store# are integers,
//      statusCode is sometimes numeric) — and `$regex` doesn't apply to a
//      numeric field; and
//   2. it must be a prefix, not equality.
// Both are solved by coercing the field to a string in-query and prefix-
// matching it: `$expr: { $regexMatch: { input: { $toString: <field> }, regex:
// "^<value>" } }` (case-insensitive). `$ifNull` guards null/missing fields so
// the whole query never errors. Verified live: "206" → 964 invoices.
//
// `_id` is the exception — it's an ObjectId the query Lambda revives from a
// FULL hex string for an index-backed exact lookup, so a partial prefix isn't
// meaningful; `_id` stays exact.
//
// Pure + tested so the query shape can't silently regress.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function buildMongoQuickFilter(rawFilter, searchField, searchValue) {
  const base = rawFilter && typeof rawFilter === "object" ? rawFilter : {};
  if (!searchField || searchValue == null) return base;
  const value = String(searchValue).trim();
  if (value === "") return base;

  // _id: exact (Lambda revives the full hex to an ObjectId).
  if (searchField === "_id") {
    return { ...base, _id: searchValue };
  }

  // Everything else: case-insensitive PREFIX on the field-as-string.
  return {
    ...base,
    $expr: {
      $regexMatch: {
        input: { $toString: { $ifNull: [`$${searchField}`, ""] } },
        regex: `^${escapeRegex(value)}`,
        options: "i",
      },
    },
  };
}
